const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8502/api';

function getToken() { return localStorage.getItem('handball_token'); }
function getUser()  { 
  try { return JSON.parse(localStorage.getItem('handball_user') || 'null'); } 
  catch { return null; } 
}

/*
 * Équipe courante.
 * Chaque requête porte l'équipe consultée dans l'en-tête X-Equipe. Le serveur
 * vérifie de son côté que l'utilisateur y a droit : ce choix ne donne accès à
 * rien de plus, il désigne seulement laquelle de ses équipes on regarde.
 */
const CLE_EQUIPE = 'handball_equipe';
export function getEquipeCourante() {
  const v = parseInt(localStorage.getItem(CLE_EQUIPE), 10);
  return Number.isFinite(v) ? v : null;
}
export function setEquipeCourante(id) {
  if (id) localStorage.setItem(CLE_EQUIPE, String(id));
  else localStorage.removeItem(CLE_EQUIPE);
}

async function apiFetch(endpoint, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const equipe = getEquipeCourante();
  if (equipe) headers['X-Equipe'] = String(equipe);
  const res = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const e = new Error(err.error || 'Erreur serveur');
    e.code = err.code; e.status = res.status; e.detail = err.detail;   // détail d'un service tiers (Kwixéo)
    // Nouvelle version des conditions publiée pendant la session : on les représente
    if (err.code === 'CONDITIONS') window.dispatchEvent(new Event('conditions-requises'));
    throw e;
  }
  return res.json();
}

// Auth
export async function login(username, password) {
  const data = await apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) });
  localStorage.setItem('handball_token', data.token);
  localStorage.setItem('handball_user', JSON.stringify({
    username: data.username, role: data.role, joueur_id: data.joueur_id,
    club_id: data.club_id ?? null, club_nom: data.club_nom ?? null, equipes: data.equipes || []
  }));
  // Équipe par défaut : la première du compte, sauf si celle mémorisée est toujours valide
  const courante = getEquipeCourante();
  if (data.role !== 'super_admin' && (!courante || !(data.equipes || []).includes(courante)))
    setEquipeCourante((data.equipes || [])[0] || null);
  return data;
}
export function logout() {
  localStorage.removeItem('handball_token');
  localStorage.removeItem('handball_user');
  // L'équipe consultée est effacée aussi : sur un appareil partagé, rien ne doit révéler
  // à quel club il a servi. Les pages publiques d'un club passent par leur lien (?equipe=…).
  localStorage.removeItem(CLE_EQUIPE);
}
export function isLoggedIn() {
  const token = getToken();
  if (!token) return false;
  try { const p = JSON.parse(atob(token.split('.')[1])); return p.exp * 1000 > Date.now(); } catch { return false; }
}
export function getCurrentUser() { return getUser(); }
export function hasRole(...roles) {
  const user = getUser();
  if (!user) return false;
  // admin et admin_3mt : anciens noms, conservés le temps que les jetons expirent
  const h = { super_admin: 5, admin_club: 4, admin: 4, coach: 3, tresorier: 2, admin_3mt: 2, joueur: 1 };
  const userLevel = h[user.role] || 0;
  return roles.some(r => r === user.role || (r !== 'super_admin' && userLevel >= (h[r] || 0)));
}

// Stats
export const getSaisons     = () => apiFetch('/stats/saisons');
export const getStatsSaison = (s) => apiFetch(`/stats/saison${s ? `?saison=${encodeURIComponent(s)}` : ''}`);
export const getStatsJoueur = (nom, s) => apiFetch(`/stats/joueur/${encodeURIComponent(nom)}${s ? `?saison=${encodeURIComponent(s)}` : ''}`);
export const getStatsGardien = (nom, s) => apiFetch(`/stats/gardien/${encodeURIComponent(nom)}${s ? `?saison=${encodeURIComponent(s)}` : ''}`);

// Matches
export const getMatches = (s) => apiFetch(`/matches${s ? `?saison=${encodeURIComponent(s)}` : ''}`);
export const getMatch   = (id) => apiFetch(`/matches/${id}`);

// Joueurs
export const getJoueurs  = () => apiFetch('/joueurs');
export const deleteJoueur        = (id) => apiFetch(`/joueurs/${id}`, { method:'DELETE' });
export const getHistoMaillots    = () => apiFetch('/joueurs/maillots/historique');
export const getMaillotsJoueur   = (id) => apiFetch(`/joueurs/${id}/maillots`);
export const addLavage           = (id, data) => apiFetch(`/joueurs/${id}/maillots`, { method:'POST', body: JSON.stringify(data||{}) });
export const deleteLavage        = (lavageId) => apiFetch(`/joueurs/maillots/${lavageId}`, { method:'DELETE' });
export const creerCompteJoueur   = (id, data) => apiFetch(`/joueurs/${id}/compte`, { method:'POST', body: JSON.stringify(data||{}) });
export const resetPasswordJoueur = (id) => apiFetch(`/joueurs/${id}/reset-password`, { method:'POST' });
export const forgotPassword      = (email) => apiFetch('/auth/forgot-password', { method:'POST', body: JSON.stringify({ email }) });
export const checkResetToken     = (token) => apiFetch(`/auth/reset-password/${token}`);
export const applyResetPassword  = (token, password) => apiFetch(`/auth/reset-password/${token}`, { method:'POST', body: JSON.stringify({ password }) });
export const getJoueur   = (id) => apiFetch(`/joueurs/${id}`);
export const createJoueur = (data) => apiFetch('/joueurs', { method: 'POST', body: JSON.stringify(data) });
export const updateJoueur = (id, data) => apiFetch(`/joueurs/${id}`, { method: 'PUT', body: JSON.stringify(data) });

// Users
export const changePassword = (cur, nxt) => apiFetch('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword: cur, newPassword: nxt }) });

// Présences
export const getGymnases    = () => apiFetch('/gymnases');
export const createGymnase  = (data) => apiFetch('/gymnases', { method:'POST', body: JSON.stringify(data) });
export const updateGymnase  = (id, data) => apiFetch(`/gymnases/${id}`, { method:'PUT', body: JSON.stringify(data) });
export const deleteGymnase  = (id) => apiFetch(`/gymnases/${id}`, { method:'DELETE' });

export const getEntrainements = () => apiFetch('/presences/entrainements');
export const getEntrainement  = (id) => apiFetch(`/presences/entrainements/${id}`);
export const createEntrainement = (data) => apiFetch('/presences/entrainements', { method: 'POST', body: JSON.stringify(data) });
export const setPresenceEnt = (entId, joueurId, data) => apiFetch(`/presences/entrainements/${entId}/presence/${joueurId}`, { method: 'PUT', body: JSON.stringify(data) });
export const getPresencesMatch = (matchId) => apiFetch(`/presences/matchs?match_id=${matchId}`);
export const initPresencesMatch = (matchId) => apiFetch(`/presences/matchs/init/${matchId}`, { method: 'POST' });
export const setPresenceMatch = (matchId, joueurId, data) => apiFetch(`/presences/matchs/${matchId}/presence/${joueurId}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteEntrainement = (id) => apiFetch(`/presences/entrainements/${id}`, { method: 'DELETE' });
export const getRencontres    = () => apiFetch('/presences/rencontres');
export const getRencontre     = (id) => apiFetch(`/presences/rencontres/${id}`);
export const createRencontre  = (data) => apiFetch('/presences/rencontres', { method: 'POST', body: JSON.stringify(data) });
export const deleteRencontre  = (id) => apiFetch(`/presences/rencontres/${id}`, { method: 'DELETE' });
export const setPresenceRenc  = (rid, jid, data) => apiFetch(`/presences/rencontres/${rid}/presence/${jid}`, { method: 'PUT', body: JSON.stringify(data) });
export const getStatsPresences = (joueurId) => apiFetch(`/presences/stats/${joueurId}`);
export const getMesConvocations    = () => apiFetch('/presences/mes-convocations');
export const repondreEntrainement  = (id, data) => apiFetch(`/presences/entrainements/${id}/reponse`, { method: 'PUT', body: JSON.stringify(data) });
export const repondreRencontre     = (id, data) => apiFetch(`/presences/rencontres/${id}/reponse`, { method: 'PUT', body: JSON.stringify(data) });
export const setCovoiturage        = (rid, jid, data) => apiFetch(`/presences/rencontres/${rid}/covoiturage/${jid}`, { method: 'PUT', body: JSON.stringify(data) });

// Caisse noire
export const getCategoriesCN     = () => apiFetch('/caisse-noire/categories');
export const createCategorieCN   = (data) => apiFetch('/caisse-noire/categories', { method:'POST', body: JSON.stringify(data) });
export const updateCategorieCN   = (id, data) => apiFetch(`/caisse-noire/categories/${id}`, { method:'PUT', body: JSON.stringify(data) });
export const deleteCategorieCN   = (id) => apiFetch(`/caisse-noire/categories/${id}`, { method:'DELETE' });
export const getCaisseNnoireRegles = () => apiFetch('/caisse-noire/regles');
export const createRegle         = (data) => apiFetch('/caisse-noire/regles', { method:'POST', body: JSON.stringify(data) });
export const updateRegle         = (id, data) => apiFetch(`/caisse-noire/regles/${id}`, { method:'PUT', body: JSON.stringify(data) });
export const deleteRegle         = (id) => apiFetch(`/caisse-noire/regles/${id}`, { method:'DELETE' });
export const getDefis            = () => apiFetch('/caisse-noire/defis');
export const createDefi          = (data) => apiFetch('/caisse-noire/defis', { method:'POST', body: JSON.stringify(data) });
export const updateDefi          = (id, data) => apiFetch(`/caisse-noire/defis/${id}`, { method:'PUT', body: JSON.stringify(data) });
export const deleteDefi          = (id) => apiFetch(`/caisse-noire/defis/${id}`, { method:'DELETE' });
export const tirerDefi           = (joueurId) => apiFetch(`/caisse-noire/defis/tirage${joueurId ? `?joueur_id=${joueurId}` : ''}`);
export const getQuotaDefis       = () => apiFetch('/caisse-noire/defis/quota');
export const getSoldesCaisseNoire = () => apiFetch('/caisse-noire/soldes');
export const getTotalCaisseNoire  = () => apiFetch('/caisse-noire/total');
export const getTransactionsJoueur = (id) => apiFetch(`/caisse-noire/joueur/${id}`);
export const getImpayees         = () => apiFetch('/caisse-noire/impayees');
export const addAmende           = (data) => apiFetch('/caisse-noire/amende', { method:'POST', body: JSON.stringify(data) });
export const annulerAmende       = (id, defi, force) => apiFetch(`/caisse-noire/annuler/${id}`, { method:'PUT', body: JSON.stringify({ defi, force }) });
export const reactiverAmende     = (id) => apiFetch(`/caisse-noire/reactiver/${id}`, { method:'PUT' });
export const transfererAmende    = (id, joueur_id) => apiFetch(`/caisse-noire/transferer/${id}`, { method:'PUT', body: JSON.stringify({ joueur_id }) });
export const payerAmendes        = (ids, payee=true) => apiFetch('/caisse-noire/payer', { method:'PUT', body: JSON.stringify({ ids, payee }) });
export const payerTout           = (joueurId) => apiFetch(`/caisse-noire/payer-tout/${joueurId}`, { method:'PUT' });
export const deleteTransaction   = (id) => apiFetch(`/caisse-noire/${id}`, { method:'DELETE' });


// Caisse 3MT
export const getContributions3mt = () => apiFetch('/caisse-3mt/contributions');
export const getSolde3mt         = () => apiFetch('/caisse-3mt/solde');
export const getVersementsJoueur = (id) => apiFetch(`/caisse-3mt/contributions/${id}`);
export const addVersements3mt    = (data) => apiFetch('/caisse-3mt/versements', { method:'POST', body: JSON.stringify(data) });
export const getTransactions3mt = () => apiFetch('/caisse-3mt/transactions');
export const addEntree3mt      = (data) => apiFetch('/caisse-3mt/entree', { method: 'POST', body: JSON.stringify(data) });
export const addDepense3mt     = (data) => apiFetch('/caisse-3mt/depense', { method: 'POST', body: JSON.stringify(data) });
export const deleteTransaction3mt = (id) => apiFetch(`/caisse-3mt/${id}`, { method: 'DELETE' });

// Admin (PDF)
export async function uploadPDF(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('pdf', file);
  const headers = { 'Authorization': `Bearer ${token}` };
  const equipe = getEquipeCourante();
  if (equipe) headers['X-Equipe'] = String(equipe);
  const res = await fetch(`${API_URL}/admin/upload`, { method: 'POST', headers, body: formData });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Erreur upload'); }
  return res.json();
}
export const deleteMatch = (id) => apiFetch(`/admin/matches/${id}`, { method: 'DELETE' });
export const getPDFs     = () => apiFetch('/admin/pdfs');

// Club et équipes
export const getMonClub        = () => apiFetch('/clubs/mon/club');
export const getToutesEquipes  = () => apiFetch('/clubs/outils/equipes');
export const getEquipePublique = () => apiFetch('/clubs/public/equipe');

// Apparence du club
export const urlLogo = chemin => chemin ? `${API_URL}${chemin}` : null;
// clubId n'est utile qu'au super admin ; pour un admin de club, le serveur prend son club
export const setApparence = (couleur, couleur2, clubId) =>
  apiFetch('/clubs/mon/apparence', { method: 'PUT',
    body: JSON.stringify({ couleur, couleur2: couleur2 || null, ...(clubId ? { club_id: clubId } : {}) }) });
export const deleteLogo = clubId =>
  apiFetch(`/clubs/mon/logo${clubId ? `?club_id=${clubId}` : ''}`, { method: 'DELETE' });

/**
 * Redimensionne une image dans le navigateur avant l'envoi.
 * Le logo n'a jamais besoin de plus de 512 pixels : on évite d'envoyer une
 * photo de 6 Mo prise au téléphone. Transparence conservée (PNG ou WebP).
 * Le SVG est refusé ici aussi : il peut contenir du code exécutable.
 */
export function redimensionnerLogo(fichier, max = 512) {
  return new Promise((resolve, reject) => {
    if (!/^image\/(png|jpeg|webp)$/.test(fichier.type))
      return reject(new Error('Format non accepté : PNG, JPEG ou WebP uniquement'));
    const url = URL.createObjectURL(fichier);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const r = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * r);
      canvas.height = Math.round(img.height * r);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      // WebP si le navigateur sait l'encoder, PNG sinon — les deux gardent la transparence
      canvas.toBlob(b => {
        if (b && b.type === 'image/webp') return resolve(b);
        canvas.toBlob(p => p ? resolve(p) : reject(new Error('Image illisible')), 'image/png');
      }, 'image/webp', 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image illisible')); };
    img.src = url;
  });
}

export async function uploadLogo(fichier, clubId) {
  const blob = await redimensionnerLogo(fichier);
  const fd = new FormData();
  if (clubId) fd.append('club_id', String(clubId));
  fd.append('logo', blob, blob.type === 'image/webp' ? 'logo.webp' : 'logo.png');
  const headers = { Authorization: `Bearer ${getToken()}` };
  const res = await fetch(`${API_URL}/clubs/mon/logo`, { method: 'POST', headers, body: fd });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Envoi impossible'); }
  return res.json();
}

// Console éditeur
export const getClubs          = () => apiFetch('/clubs');
export const getClubDetail     = id => apiFetch(`/clubs/${id}`);
export const majClub           = (id, data) => apiFetch(`/clubs/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const creerClubComplet  = data => apiFetch('/clubs/outils/creer-club', { method: 'POST', body: JSON.stringify(data) });
export const rechercherCompte  = q => apiFetch(`/clubs/outils/rechercher-compte?q=${encodeURIComponent(q)}`);
export const resetMdpEditeur   = userId => apiFetch(`/clubs/outils/reset-mdp/${userId}`, { method: 'PUT' });
export const setAbonnement    = (clubId, data) => apiFetch(`/clubs/outils/club/${clubId}/abonnement`, { method: 'PUT', body: JSON.stringify(data) });
export const roleCompte       = (userId, role) => apiFetch(`/clubs/outils/compte/${userId}/role`, { method: 'PUT', body: JSON.stringify({ role }) });
export const activerCompte     = (userId, actif) => apiFetch(`/clubs/outils/compte/${userId}/actif`, { method: 'PUT', body: JSON.stringify({ actif }) });

// Espace club : équipes et encadrement
const qClub = id => id ? `?club_id=${id}` : '';
export const getEquipesClub    = clubId => apiFetch(`/clubs/mon/equipes${qClub(clubId)}`);
export const creerEquipe       = data => apiFetch('/clubs/mon/equipes', { method: 'POST', body: JSON.stringify(data) });
export const majEquipe         = (id, data) => apiFetch(`/clubs/mon/equipes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const rattacher         = (equipeId, userId) => apiFetch(`/clubs/mon/equipes/${equipeId}/membres`, { method: 'POST', body: JSON.stringify({ user_id: userId }) });
export const detacher          = (equipeId, userId) => apiFetch(`/clubs/mon/equipes/${equipeId}/membres/${userId}`, { method: 'DELETE' });
export const getComptesClub    = clubId => apiFetch(`/clubs/mon/comptes${qClub(clubId)}`);
export const creerCompteStaff  = data => apiFetch('/clubs/mon/comptes', { method: 'POST', body: JSON.stringify(data) });
export const majCompteClub     = (id, data) => apiFetch(`/clubs/mon/comptes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const resetCompteClub   = id => apiFetch(`/clubs/mon/comptes/${id}/reset`, { method: 'POST' });

// Conditions d'utilisation et protection des données
export const getConditions      = () => apiFetch('/auth/conditions');
export const getEtatConditions  = () => apiFetch('/auth/conditions/etat');
export const accepterConditions = version => apiFetch('/auth/conditions/accepter', { method: 'POST', body: JSON.stringify({ version }) });
export const relancerParent     = () => apiFetch('/auth/conditions/relancer-parent', { method: 'POST' });
export const getValidationParent = token => apiFetch(`/auth/parent/${token}`);
export const validerParent      = (token, version) =>
  apiFetch(`/auth/parent/${token}`, { method: 'POST', body: JSON.stringify({ version, representant: true }) });
export const setVisibiliteStats = (stats_publiques, clubId) =>
  apiFetch('/clubs/mon/visibilite', { method: 'PUT', body: JSON.stringify({ stats_publiques, ...(clubId ? { club_id: clubId } : {}) }) });

// Préférences du joueur
export const getPreferences = () => apiFetch('/joueurs/moi/preferences');
export const setPreferences = data => apiFetch('/joueurs/moi/preferences', { method: 'PUT', body: JSON.stringify(data) });

// Inscription en libre-service et demande d'abonnement
export const inscrire             = data  => apiFetch('/inscription', { method: 'POST', body: JSON.stringify(data) });
export const confirmerInscription = token => apiFetch(`/inscription/confirmer/${token}`, { method: 'POST' });
export const getDemandeAbonnement = ()    => apiFetch('/clubs/mon/demande-abonnement');
export const demanderAbonnement   = data  => apiFetch('/clubs/mon/demande-abonnement', { method: 'POST', body: JSON.stringify(data) });

// Covoiturage : qui monte avec qui
export const getCovoiturage  = rid => apiFetch(`/presences/rencontres/${rid}/covoiturage`);
export const monterVoiture   = (rid, conducteurId) =>
  apiFetch(`/presences/rencontres/${rid}/covoiturage/monter`, { method: 'PUT', body: JSON.stringify({ conducteur_id: conducteurId }) });
export const affecterVoiture = (rid, joueurId, conducteurId) =>
  apiFetch(`/presences/rencontres/${rid}/covoiturage/${joueurId}/affecter`, { method: 'PUT', body: JSON.stringify({ conducteur_id: conducteurId }) });

// Offre fondateur
export const getFondateur = () => apiFetch('/clubs/public/fondateur');
export const setFondateur = (clubId, fondateur) =>
  apiFetch(`/clubs/outils/club/${clubId}/fondateur`, { method: 'PUT', body: JSON.stringify({ fondateur }) });

// Console : comptes d'un club, regroupés par équipe
export const getComptesClubConsole = clubId => apiFetch(`/clubs/outils/club/${clubId}/comptes`);

// Caisses d'équipe : niveau d'accès et visibilité pour la direction du club
export const getAccesCaisses = () => apiFetch('/caisse-noire/acces');
export const setVisibiliteCaisses = visibilite =>
  apiFetch('/caisse-noire/visibilite', { method: 'PUT', body: JSON.stringify({ visibilite }) });
export const getTotauxCaisseNoire = () => apiFetch('/caisse-noire/total');
export const getTotauxCaisse3mt = () => apiFetch('/caisse-3mt/solde');
export const setActivationCaisses = actives =>
  apiFetch('/caisse-noire/activation', { method: 'PUT', body: JSON.stringify({ actives }) });

// Facturation Kwixéo (console de l'éditeur)
export const getEtatKwixeo   = () => apiFetch('/facturation/etat');
export const essaiKwixeo     = formule => apiFetch('/facturation/essai', { method: 'POST', body: JSON.stringify({ formule }) });
export const creerKwixeoClub = clubId => apiFetch(`/facturation/club/${clubId}/creer`, { method: 'POST' });
export const verifierKwixeoClub = clubId => apiFetch(`/facturation/club/${clubId}/verifier`, { method: 'POST' });
export const supprimerClubDefinitivement = (clubId, confirmation) =>
  apiFetch(`/clubs/outils/club/${clubId}`, { method: 'DELETE', body: JSON.stringify({ confirmation }) });
export const synchroniserKwixeoClub = clubId => apiFetch(`/facturation/club/${clubId}/synchroniser`, { method: 'POST' });

// Messages du coach ou du club (information descendante)
export const getAnnonces       = saison => apiFetch(`/annonces${saison ? `?saison=${encodeURIComponent(saison)}` : ''}`);
export const getAnnoncesNonLues = () => apiFetch('/annonces/non-lues');
export const marquerAnnonceLue = id => apiFetch(`/annonces/${id}/lu`, { method: 'POST' });
export const getLecturesAnnonce = id => apiFetch(`/annonces/${id}/lectures`);
export const modifierAnnonce   = (id, data) => apiFetch(`/annonces/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const supprimerAnnonce  = id => apiFetch(`/annonces/${id}`, { method: 'DELETE' });
export async function publierAnnonce({ titre, texte, portee, equipes = [], important, epingle, pieces = [] }) {
  const fd = new FormData();
  fd.append('titre', titre); fd.append('texte', texte || ''); fd.append('portee', portee);
  equipes.forEach(e => fd.append('equipes', String(e)));
  if (important) fd.append('important', '1');
  if (epingle) fd.append('epingle', '1');
  pieces.forEach(f => fd.append('pieces', f));
  const res = await fetch(`${API_URL}/annonces`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Publication impossible'); }
  return res.json();
}
/** Pièce jointe : téléchargée avec l'authentification, puis ouverte. */
export async function ouvrirPieceAnnonce(id) {
  const res = await fetch(`${API_URL}/annonces/pieces/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error('Pièce jointe introuvable');
  const url = URL.createObjectURL(await res.blob());
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Tâches tournantes de l'équipe
export const getTaches          = () => apiFetch('/taches');
export const creerTache         = d => apiFetch('/taches', { method: 'POST', body: JSON.stringify(d) });
export const modifierTache      = (id, d) => apiFetch(`/taches/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const supprimerTache     = id => apiFetch(`/taches/${id}`, { method: 'DELETE' });
export const repartirTaches     = () => apiFetch('/taches/repartir', { method: 'POST' });
export const reattribuerTache   = (aid, joueur_id) => apiFetch(`/taches/affectations/${aid}`, { method: 'PUT', body: JSON.stringify({ joueur_id }) });
export const indisponibleTache  = aid => apiFetch(`/taches/affectations/${aid}/indisponible`, { method: 'POST' });

// « Bientôt » : projets à venir
export const getProjets        = () => apiFetch('/projets');
export const interetProjet     = id => apiFetch(`/projets/${id}/interet`, { method: 'POST' });
export const getProjetsConsole = () => apiFetch('/projets/console');
export const creerProjet       = d => apiFetch('/projets', { method: 'POST', body: JSON.stringify(d) });
export const modifierProjet    = (id, d) => apiFetch(`/projets/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const supprimerProjet   = id => apiFetch(`/projets/${id}`, { method: 'DELETE' });
export const getAnniversairesEquipe = () => apiFetch('/presences/anniversaires');

// Aide : FAQ, suggestions, signalements et questions au club
export const getFaq = () => apiFetch('/faq');
export async function envoyerRetour({ type, message, capture, page, version, ecran }) {
  const fd = new FormData();
  fd.append('type', type); fd.append('message', message);
  if (page) fd.append('page', page); if (version) fd.append('version', version); if (ecran) fd.append('ecran', ecran);
  if (capture) fd.append('capture', capture);
  const res = await fetch(`${API_URL}/retours`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Envoi impossible'); }
  return res.json();
}
export const getRetoursConsole = () => apiFetch('/retours/console');
export const statutRetour      = (id, statut) => apiFetch(`/retours/${id}`, { method: 'PUT', body: JSON.stringify({ statut }) });
export const supprimerRetour   = id => apiFetch(`/retours/${id}`, { method: 'DELETE' });
export async function ouvrirCaptureRetour(id) {
  const res = await fetch(`${API_URL}/retours/${id}/capture`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error('Capture introuvable');
  const url = URL.createObjectURL(await res.blob()); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
}
export const getFaqConsole = () => apiFetch('/faq/console');
export const creerFaq      = d => apiFetch('/faq', { method: 'POST', body: JSON.stringify(d) });
export const modifierFaq   = (id, d) => apiFetch(`/faq/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const supprimerFaq  = id => apiFetch(`/faq/${id}`, { method: 'DELETE' });
export const relancerRencontre    = id => apiFetch(`/presences/rencontres/${id}/relancer`, { method: 'POST' });
export const relancerEntrainement = id => apiFetch(`/presences/entrainements/${id}/relancer`, { method: 'POST' });
export const getContexteMaillots = () => apiFetch('/joueurs/maillots/contexte');

// Visite de découverte à la première connexion
export const VISITE_VERSION = '1';
export const getVisite = () => apiFetch('/visite');
export const marquerVisiteVue = () => apiFetch('/visite', { method: 'POST', body: JSON.stringify({ version: VISITE_VERSION }) });

// Compte et modération (exigences d'Apple pour les applications des stores)
export const supprimerMonCompte = d => apiFetch('/compte/supprimer', { method: 'POST', body: JSON.stringify(d) });
export const signalerAnnonce = (id, d) => apiFetch(`/annonces/${id}/signaler`, { method: 'POST', body: JSON.stringify(d) });
